/**
 * Interactive tutorials — the framework-agnostic half of the overlay engine.
 *
 * Everything here is a pure function of numbers, strings or the DOM: where the
 * four scrim rectangles go, where the card sits, which checkpoint "Skip to step
 * N" lands on, how a body string becomes safe HTML. TourOverlay.vue owns the
 * reactive state and the listeners and calls into this file; keeping the maths
 * out of the component is what makes the geometry testable at all, since jsdom
 * reports every element as 0×0 and lays nothing out.
 *
 * Ported from design_handoff_interactive_tutorial/blume-live-tour.jsx
 * (`TourOverlay`), with the deviations noted per function.
 */

import type { TourCheckpoint } from './registry'

/** A viewport-relative box, in CSS pixels, as the overlay renders it. */
export interface Box {
  top: number
  left: number
  width: number
  height: number
}

/** Padding between the target's own rect and the spotlight ring. */
export const SPOTLIGHT_PAD = 6

/** Card widths — anchored beside a target, and the wider centred intro card. */
export const CARD_WIDTH = 300
export const INTRO_CARD_WIDTH = 400

/**
 * Below this the anchored card has nowhere to go beside a target, so the engine
 * docks it bottom-right instead of ending the run (handoff README, "Interactions
 * & edge behaviour"). The tour is only ever auto-started ≥1024px, but a florist
 * can resize into this mid-run.
 */
export const MIN_TOUR_VIEWPORT = 1024

/** How long a checkpoint waits for a target that isn't in the DOM before skipping. */
export const MISSING_TARGET_TIMEOUT_MS = 5000

/** Let the app's own click handler run before advancing off its checkpoint. */
export const CLICK_ADVANCE_DELAY_MS = 60

/**
 * Why a run ended.
 *
 * `link` — the card's own "Exit tutorial": the page stays, so the toast can be
 * shown where it is.
 * `nav`  — a header link that replaces this document: anything rendered now is
 * destroyed before it can be read, so the toast has to be carried over.
 */
export type TourExitReason = 'link' | 'nav'

/**
 * Whether clicking this link replaces the CURRENT document.
 *
 * The header's logo is `target="_blank"` (app/views/application/_navbar.html.erb),
 * so it opens a tab and leaves this page exactly where it is. Treating that as
 * navigation would save a toast for "the next page load" and pop it minutes
 * later on an unrelated screen.
 */
export function navigatesAwayFrom(anchor: HTMLAnchorElement): boolean {
  const target = (anchor.getAttribute('target') || '').trim().toLowerCase()
  return target === '' || target === '_self'
}

/** The one element carrying this `data-tour` id, or null. */
export function tourEl(target?: string | null): HTMLElement | null {
  if (!target) return null
  return document.querySelector<HTMLElement>(`[data-tour="${target}"]`)
}

/**
 * Bottom edge of the sticky top nav, so the scrim starts BELOW it and the
 * header's own links stay clickable — clicking one ends the tutorial rather
 * than being swallowed by a full-screen scrim (handoff README §3).
 */
export function headerBottom(): number {
  const nav = document.querySelector('.top-nav')
  if (!nav) return 0
  return Math.max(0, Math.round(nav.getBoundingClientRect().bottom))
}

/**
 * The hole the scrim leaves and the spotlight rings: the target's rect grown by
 * SPOTLIGHT_PAD on every side.
 *
 * A DEGENERATE RECT IS TREATED AS NO TARGET AT ALL, which the prototype did not
 * do. An element that is display:none, still animating in (the arrangement card
 * is added with a transition) or detached measures 0×0 at 0,0 — and padding
 * that produces a 12px hole pinned to the top-left corner of the viewport,
 * which is both a spotlight on nothing and a 12px gap in the scrim that a click
 * leaks through. Returning null instead routes the checkpoint through the
 * missing-target path: dock the card, keep polling, and light up properly once
 * the element has a size (docs/INTERACTIVE_TUTORIALS_PLAN.md §8, risk areas).
 */
export function spotlightHole(rect: Box | null): Box | null {
  if (!rect) return null
  if (rect.width <= 0 || rect.height <= 0) return null

  return {
    top: rect.top - SPOTLIGHT_PAD,
    left: rect.left - SPOTLIGHT_PAD,
    width: rect.width + SPOTLIGHT_PAD * 2,
    height: rect.height + SPOTLIGHT_PAD * 2,
  }
}

/**
 * The scrim as FOUR rectangles around the hole (above / below / left / right)
 * rather than one element with a cut-out, so the target itself is never covered
 * and stays genuinely clickable — the whole mechanic depends on the florist
 * clicking the real button.
 *
 * With no hole it is one rectangle covering everything below the header. Every
 * dimension is floored at 0: a target flush against an edge produces a
 * zero-width strip, never a negative one that a browser would render as a
 * full-width band across the target.
 */
export function scrimRects(hole: Box | null, vw: number, vh: number, hb: number): Box[] {
  if (!hole) {
    return [{ top: hb, left: 0, width: vw, height: Math.max(0, vh - hb) }]
  }

  const holeBottom = hole.top + hole.height
  const holeRight = hole.left + hole.width
  // The side strips only span the hole's own band, and that band starts at the
  // header when the hole runs up underneath it.
  const bandTop = Math.max(hb, hole.top)
  const bandHeight = Math.max(0, holeBottom - bandTop)
  // The lower strip is clamped to the header for the SAME reason as bandTop,
  // and this is the case that is easy to miss: once the florist scrolls the
  // target up behind the sticky nav, holeBottom is ABOVE the header's bottom
  // edge, and an unclamped strip starts there — laying scrim over the header
  // itself. That covers the header links with a z-index 100000 blurred panel,
  // so the "click a header link to leave the tutorial" exit stops working on
  // the one gesture a stuck florist reaches for.
  const belowTop = Math.max(hb, holeBottom)

  return [
    { top: hb, left: 0, width: vw, height: Math.max(0, hole.top - hb) },
    { top: belowTop, left: 0, width: vw, height: Math.max(0, vh - belowTop) },
    { top: bandTop, left: 0, width: Math.max(0, hole.left), height: bandHeight },
    { top: bandTop, left: holeRight, width: Math.max(0, vw - holeRight), height: bandHeight },
  ]
}

/** Where the anchored step card goes, and where its tail points. */
export interface CardPlacement extends Box {
  /** True when the card had to flip above the target to fit. */
  above: boolean
  /** Tail offset from the card's left edge, clamped inside the card. */
  tailLeft: number
}

/**
 * Card placement, option C1 from the handoff: beside/below the spotlit target,
 * flipped above when it won't fit below, clamped into the viewport, with the
 * tail aligned to the target's centre.
 *
 * Returns null when there is no hole — the caller docks the card bottom-right.
 */
export function cardPlacement(
  hole: Box | null,
  vw: number,
  vh: number,
  hb: number,
  cardHeight: number,
  cardWidth: number = CARD_WIDTH
): CardPlacement | null {
  if (!hole) return null

  const gap = 16
  const margin = 12
  const holeBottom = hole.top + hole.height

  const fitsBelow = holeBottom + gap + cardHeight < vh - margin
  const above = !fitsBelow && hole.top - gap - cardHeight > hb

  const left = Math.max(margin, Math.min(hole.left, vw - cardWidth - margin))
  const top = above
    ? hole.top - gap - cardHeight
    : Math.min(holeBottom + gap, vh - cardHeight - margin)

  // 6px is half the rotated 12px tail square, so this is the tail's centre.
  const tailLeft = Math.max(
    18,
    Math.min(hole.left + hole.width / 2 - left - 6, cardWidth - 30)
  )

  return { top, left, width: cardWidth, height: cardHeight, above, tailLeft }
}

/**
 * How far to scroll so a target isn't jammed against the top of the viewport or
 * hidden behind the card, or null if it is already comfortable.
 *
 * window.scrollBy rather than scrollIntoView (handoff README §3): the target may
 * be inside a scrollable panel, and scrollIntoView would scroll the nearest
 * ancestor — moving the element out from under a spotlight positioned in
 * viewport coordinates.
 */
export function autoScrollDelta(rect: Box, vh: number): number | null {
  const bottom = rect.top + rect.height
  if (rect.top >= 120 && bottom <= vh - 240) return null
  return rect.top - Math.min(220, vh * 0.3)
}

/** Result of "Skip to step N" — either a checkpoint index, or the tour is over. */
export type SkipTarget = { kind: 'index'; index: number } | { kind: 'complete' }

/**
 * Where an intro card's "Skip to step N" lands.
 *
 * Not simply the first checkpoint of the next step: a step usually ENDS with the
 * click that changes screen (`qstep-arrangements`, `qstep-review`), and skipping
 * past that would leave the tour pointing at a screen the florist is not on. So
 * when the step being skipped ends on a `qstep-*` click, land there — the
 * florist still drives the screen change themselves. On the last step there is
 * nothing to skip to and the tour completes.
 */
export function skipStepTarget(checkpoints: TourCheckpoint[], index: number): SkipTarget {
  const current = checkpoints[index]
  if (!current) return { kind: 'complete' }

  const next = checkpoints.findIndex((cp, k) => k > index && cp.step > current.step)
  const last = next < 0 ? checkpoints.length - 1 : next - 1

  if (last > index && /^qstep-/.test(checkpoints[last].target || '')) {
    return { kind: 'index', index: last }
  }
  if (next < 0) return { kind: 'complete' }
  return { kind: 'index', index: next }
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

/**
 * Checkpoint copy → safe HTML: escape everything, THEN promote `**…**` to <b>.
 *
 * The order is the whole point. The copy in registry.ts is plain text and is
 * expected to become florist-editable, so it is never trusted: by the time the
 * bold pass runs there is no markup left in the string for a `<b>` of ours to
 * be confused with. The result is the only string this feature ever hands to
 * v-html, and it can contain nothing but the tags added on the last line.
 */
export function renderMarkdownLite(text: string): string {
  const escaped = text.replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch])
  return escaped.replace(/\*\*([\s\S]+?)\*\*/g, '<b>$1</b>')
}
